<template>
  <v-layout justify-center>
    <v-flex md8 xs12>
      <v-layout row wrap>
        <!-- Event Title -->
        <v-flex xs12 class="mt-4">
          <v-text-field
            :error="checkFieldError('name')"
            :label="$t('fields.event_title') + '*'"
            :readonly="!editable"
            counter="300"
            outline
            ref="title"
            v-model="event.name"
          ></v-text-field>
        </v-flex>

        <!-- Event voter UI type -->
        <v-flex xs12>
          <v-radio-group
            :readonly="!editable"
            v-model="event.voter_ui_type"
            :label="$t('labels.voterUiType') + ':'"
          >
            <v-radio
              :label="$t('labels.voterUiTypeBasic')"
              value="basic"
              color="primary"
            ></v-radio>
            <v-radio
              :label="$t('labels.voterUiTypeAdvanced')"
              value="advanced"
              color="primary"
            ></v-radio>
          </v-radio-group>
        </v-flex>

        <!-- Event Authentication Method -->
        <v-flex xs12>
          <v-radio-group
            :readonly="!editable"
            v-model="event.anonymous"
            :label="$t('labels.eventType') + ':'"
            v-on:change="
              value => {
                if (value === false) {
                  $alert($t('alerts.eventTypeTransparent'), 'error')
                }
              }
            "
          >
            <v-radio
              :label="$t('labels.anonymous')"
              :value="true"
              color="primary"
            ></v-radio>
            <v-radio
              :label="$t('labels.transparent')"
              :value="false"
              color="primary"
            ></v-radio>
          </v-radio-group>
        </v-flex>

        <!-- Event Authentication Method -->
        <v-flex xs12>
          <v-radio-group
            :readonly="!editable"
            v-model="event.sms_authentication"
            :label="$t('labels.identityConfirmation') + ':'"
          >
            <v-radio
              :label="$t('labels.singleAuthentication')"
              :value="false"
              color="primary"
            ></v-radio>
            <v-radio
              :label="$t('labels.doubleAuthentication')"
              :value="true"
              color="primary"
            ></v-radio>
          </v-radio-group>
        </v-flex>

        <!-- Event Voting Type -->
        <v-flex xs12>
          <v-radio-group
            :readonly="!editable"
            v-model="event.type"
            :label="$t('fields.eventType') + ':'"
          >
            <v-radio
              :label="$t('labels.eventTypeClassic')"
              :value="'classic'"
              color="primary"
            ></v-radio>
            <v-radio
              :label="$t('labels.eventTypeWeighted')"
              :value="'weighted'"
              color="primary"
            ></v-radio>
          </v-radio-group>
        </v-flex>

        <!-- Event Results Display -->
        <v-flex xs12>
          <v-radio-group
            :readonly="!editable"
            v-model="event.results_in_progress"
            :label="$t('labels.showResults') + ':'"
          >
            <v-radio
              :label="$t('labels.results_in_progress_1')"
              :value="true"
              color="primary"
            ></v-radio>
            <v-radio
              :label="$t('labels.results_in_progress_2')"
              :value="false"
              color="primary"
            ></v-radio>
          </v-radio-group>
        </v-flex>

        <!-- Event Internal Tags -->
        <v-flex xs12 class="mt-4">
          <TagsPopup
            :ajax="true"
            :data="'events'"
            :label="$t('fields.eventTags')"
            :readonly="!editable"
            v-model="event.tags"
          ></TagsPopup>
        </v-flex>

        <!-- Event Allowed Domains -->
        <v-flex xs12 style="margin-top: 16px">
          <v-label>{{ $t('labels.allowed_domains') }}:</v-label>
          <v-checkbox
            class="mt-0"
            @click.stop="allowDomainsClick"
            color="primary"
            v-model="event.allow_all_domains"
          >
            <template v-slot:label>
              <span @click.stop="allowDomainsClick">{{
                $t('labels.allowAllDomains')
              }}</span>
            </template>
          </v-checkbox>
        </v-flex>
        <v-flex xs12 v-if="!event.allow_all_domains">
          <Tags
            :ajax="false"
            :data="[]"
            :hint="$t('labels.domainExplanation')"
            :label="$t('fields.allowed_domains')"
            :readonly="!editable || event.voters.length != 0"
            v-model="event.domains"
          ></Tags>
        </v-flex>

        <!-- Event Languages -->
        <v-flex xs12>
          <v-radio-group
            :readonly="!editable"
            v-model="event.language"
            :label="$t('labels.eventLanguage') + ':'"
          >
            <v-radio
              :key="language"
              :label="$t('languages.' + language)"
              :value="language"
              color="primary"
              v-for="language in $languages"
            ></v-radio>
          </v-radio-group>
        </v-flex>

        <!-- Event Send Results (PDF) to Voters by Email -->
        <v-flex xs12 style="margin-top: 16px">
          <v-label>{{ $t('labels.sendPdf') }}:</v-label>
          <v-radio-group
            :readonly="!editable"
            v-model="event.pdf"
            row
            class="mt-0"
          >
            <v-radio
              :label="$t('labels.yes')"
              :value="true"
              color="primary"
            ></v-radio>
            <v-radio
              :label="$t('labels.no')"
              :value="false"
              color="primary"
            ></v-radio>
          </v-radio-group>
        </v-flex>

        <!-- Enable manual reminders -->
        <v-flex xs12 style="margin-top: 16px">
          <v-label>{{ $t('labels.manual_reminders') }}:</v-label>
          <v-radio-group
            :readonly="!editable"
            v-model="event.manual_reminders"
            row
            class="mt-0"
          >
            <v-radio
              :label="$t('labels.yes')"
              :value="true"
              color="primary"
            ></v-radio>
            <v-radio
              :label="$t('labels.no')"
              :value="false"
              color="primary"
            ></v-radio>
          </v-radio-group>
        </v-flex>

        <!-- Reminder Message Details -->
        <v-flex xs12 class="mt-4" v-if="event.manual_reminders">
          <v-text-field
            :error="checkFieldError('reminder_mail_title')"
            :label="$t('fields.reminder_title') + '*'"
            :readonly="!editable"
            counter="50"
            max="50"
            outline
            ref="title"
            v-model="event.reminder_mail_title"
          ></v-text-field>
        </v-flex>
        <!-- Event Warmup Message -->
        <v-flex xs12 style="margin-top: 16px" v-if="event.manual_reminders">
          <v-textarea
            :error="checkFieldError('reminder_mail')"
            :label="$t('fields.reminder_text') + '*'"
            :readonly="!editable"
            outline
            v-model="event.reminder_mail"
          ></v-textarea>
        </v-flex>

        <!-- Enable adding voters during event active -->
        <v-flex xs12 style="margin-top: 16px">
          <v-label>{{ $t('labels.adding_voters_in_progress') }}:</v-label>
          <v-radio-group
            :readonly="!editable"
            v-model="event.adding_voters_in_progress"
            row
            class="mt-0"
          >
            <v-radio
              :label="$t('labels.yes')"
              :value="true"
              color="primary"
            ></v-radio>
            <v-radio
              :label="$t('labels.no')"
              :value="false"
              color="primary"
            ></v-radio>
          </v-radio-group>
        </v-flex>

        <!-- Event Audio Video Feature -->
        <v-flex xs12 style="margin-top: 16px" v-if="!isBasicUiType">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-label class="audio-video-label" v-bind="attrs" v-on="on"
                >{{ $t('labels.enableAudioVideo') }}:&nbsp;&nbsp;</v-label
              >
            </template>
            <span>{{ $t('tooltips.audioVideoTooltip') }}</span>
          </v-tooltip>
          <v-radio-group
            :readonly="!editable"
            v-model="event.enable_audio_video"
            row
            class="mt-0"
          >
            <v-radio
              :label="$t('labels.yes')"
              :value="true"
              color="#becbad"
            ></v-radio>
            <v-radio
              :label="$t('labels.no')"
              :value="false"
              color="#becbad"
            ></v-radio>
          </v-radio-group>
        </v-flex>

        <!-- Event Enable Live Questions -->
        <v-flex xs12 style="margin-top: 16px" v-if="!isBasicUiType">
          <v-label>{{ $t('labels.enable_live_questions') }}:</v-label>
          <v-radio-group
            :readonly="!editable"
            v-model="event.enable_live_questions"
            row
            class="mt-0"
          >
            <v-radio
              :label="$t('labels.yes')"
              :value="true"
              color="primary"
            ></v-radio>
            <v-radio
              :label="$t('labels.no')"
              :value="false"
              color="primary"
            ></v-radio>
          </v-radio-group>
        </v-flex>

        <!-- Event Live Questions Visibility -->
        <!-- Vertical -->
        <v-flex xs12 v-if="event.enable_live_questions">
          <v-radio-group
            :readonly="!editable"
            v-model="event.live_questions_visibility"
            :label="$t('labels.live_questions_visibility') + ':'"
          >
            <v-radio
              :label="$t('labels.live_questions_visibility_organizer')"
              :value="'organizer_only'"
              color="primary"
            ></v-radio>
            <v-radio
              :label="
                $t('labels.live_questions_visibility_organizer_and_voters')
              "
              :value="'organizer_and_voters'"
              color="primary"
            ></v-radio>
          </v-radio-group>
        </v-flex>

        <!-- Horizontal -->
        <!-- <v-flex
          xs12
          style="margin-top: 16px"
          v-if="event.enable_live_questions"
        >
          <v-label>{{ $t('labels.live_questions_visibility') }}:</v-label>
          <v-radio-group
            :readonly="!editable"
            v-model="event.live_questions_visibility"
            row
            class="mt-0"
          >
            <v-radio
              :label="$t('labels.live_questions_visibility_organizer')"
              :value="'organizer_only'"
              color="primary"
            ></v-radio>
            <v-radio
              :label="
                $t('labels.live_questions_visibility_organizer_and_voters')
              "
              :value="'organizer_and_voters'"
              color="primary"
            ></v-radio>
          </v-radio-group>
        </v-flex> -->

        <!-- Event Enable Quorum -->
        <v-flex xs12 style="margin-top: 16px">
          <v-label>{{ $t('labels.quorum') }}:</v-label>
          <v-radio-group
            :readonly="!editable"
            v-model="event.quorum"
            row
            class="mt-0"
          >
            <v-radio
              :label="$t('labels.yes')"
              :value="true"
              color="primary"
            ></v-radio>
            <v-radio
              :label="$t('labels.no')"
              :value="false"
              color="primary"
            ></v-radio>
          </v-radio-group>
          <v-text-field
            v-if="event.quorum"
            :error="checkFieldError('quorum_value')"
            :label="$t('fields.event_quorum_value')"
            :readonly="!editable"
            outline
            v-model="event.quorum_value"
          ></v-text-field>
        </v-flex>

        <!-- Event Warmup Message -->
        <v-flex xs12 style="margin-top: 16px">
          <v-textarea
            :label="$t('labels.warmup_message')"
            :readonly="!editable"
            outline
            v-model="event.warmup_message"
          ></v-textarea>
        </v-flex>

        <!-- Event logo -->
        <v-flex xs12 style="margin-top: 16px" v-if="editable">
          <v-btn @click="triggerLogoInputClick" color="info" small class="ml-0">
            <v-icon small>fa-upload fa-fw</v-icon>{{ $t('labels.logo_upload') }}
          </v-btn>
          <input
            @change="handleLogoInputChange"
            accept=".png,.jpg,.jpeg"
            ref="logoInput"
            style="display: none"
            type="file"
          />
        </v-flex>
        <!-- Event logo preview & delete -->
        <v-flex xs4 style="margin-top: 16px" v-if="event.logo != null">
          <v-card>
            <v-img class="white--text" :src="event.logo.link">
              <v-container fill-height fluid>
                <v-layout fill-height>
                  <v-flex xs12 align-end flexbox>
                    <span class="headline">{{ event.logo.original_name }}</span>
                  </v-flex>
                </v-layout>
              </v-container>
            </v-img>
            <v-card-actions v-if="editable">
              <v-spacer></v-spacer>
              <v-btn @click="handleLogoInputDelete" flat color="red"
                >Delete</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-flex>
      </v-layout>
    </v-flex>
  </v-layout>
</template>

<script>
import TagsPopup from '@/components/trinkets/TagsPopup.vue'
import Tags from '@/components/trinkets/Tags.vue'
import Logo from '@/models/Logo.js'
import get_file_size from '@/helpers/get_file_size.js'
import handle_validation_error from '@/helpers/handle_validation_error.js'
import handle_session_error from '@/helpers/handle_session_error.js'

export default {
  components: {
    TagsPopup,
    Tags,
  },

  props: ['editable', 'event', 'checkFieldError', 'isBasicUiType'],

  watch: {
    isBasicUiType: function (val) {
      if (val === true) {
        this.event.enable_live_questions = false
        this.event.enable_audio_video = false
      }
    },
  },

  methods: {
    allowDomainsClick() {
      if (
        this.editable &&
        this.event.voters.length != 0 &&
        this.event.allow_all_domains
      ) {
        this.$alert(this.$t('alerts.removeEmailsFirst'))
      } else {
        if (this.editable) {
          this.event.allow_all_domains = !this.event.allow_all_domains
        }
      }
    },

    triggerLogoInputClick() {
      this.$refs.logoInput.click()
    },

    handleLogoInputDelete() {
      // Clear event.logo from data.
      this.event.logo = null

      // TODO: Maybe delete the image from the server also?
    },

    handleLogoInputChange() {
      if (this.$refs.logoInput.value === null) {
        return
      }

      // If selected file is larger than 8MB, alert the user and don't upload the file.
      if (get_file_size(this.$refs.logoInput.files[0]) >= 1024 * 8) {
        this.$alert(this.$t('alerts.maxFileSizeLimit'))
        return
      }

      this.$APIUpload(
        this.$refs.logoInput.files[0],
        data => {
          this.$refs.logoInput.value = null

          if (!data.success) {
            this.$alert(this.$t('alerts.invalidImport'))
          }

          this.$set(this.event, 'logo', new Logo(data.file))
        },
        'UPLOAD',
        null,
        error => {
          handle_validation_error(error, this.$alert)
          handle_session_error(error, this.$store, this.$router, this.$prompt)
        }
      )
    },
  },
}
</script>
